import type { PdfObject } from "@/lib/pdflib2/PdfObject";
import type { Vector2d } from "@/lib/pdflib2/Vector2d";
import { vector2d } from "@/lib/pdflib2/Vector2d";
import type { PdfColor } from "@/lib/pdflib2/PdfColor";
import { v4 } from "uuid";
import { objectGroup } from "@/lib/pdflib2/PdfObjectGroup";
import type { Pdf2DObject } from "@/lib/pdflib2/Pdf2DObject";
import debug from "debug";

export interface PdfLine extends Pdf2DObject {
	start(): Vector2d;

	end(): Vector2d;

	color(): PdfColor;

	lineWidth(): number;
}

export interface PdfLineImplOptions {
	start: Vector2d;
	end: Vector2d;
	color: PdfColor;
	lineWidth: number;
}

export class PdfLineImpl implements PdfLine {
	private readonly _id: string;

	constructor(private readonly options: PdfLineImplOptions) {
		this._id = v4();
	}

	ref(): string {
		return this._id;
	}

	start(): Vector2d {
		return this.options.start;
	}

	end(): Vector2d {
		return this.options.end;
	}

	color(): PdfColor {
		return this.options.color;
	}

	lineWidth(): number {
		return this.options.lineWidth;
	}

	size(): Vector2d {
		const topLeft = vector2d(
			Math.min(this.options.start.x(), this.options.end.x()),
			Math.min(this.options.start.y(), this.options.end.y()));

		const bottomRight = vector2d(
			Math.max(this.options.start.x(), this.options.end.x()),
			Math.max(this.options.start.y(), this.options.end.y()));

		return bottomRight.add(topLeft.negate());
	}
}

export function line(start: Vector2d, end: Vector2d, color: PdfColor, lineWidth: number): PdfLine {
	logger(`Line: ${start.toString()} -> ${end.toString()}`);
	return new PdfLineImpl({
		start,
		end,
		color,
		lineWidth
	});
}

const logger = debug("pdflib:PdfLine");

export function cross(center: Vector2d, radius: number, color: PdfColor, lineWidth: number): Pdf2DObject {
	const topLeft = center.add(vector2d(-radius, -radius));
	const topRight = center.add(vector2d(radius, -radius));
	const bottomLeft = center.add(vector2d(-radius, radius));
	const bottomRight = center.add(vector2d(radius, radius));

	logger("TL:", topLeft.toString(), "TR:", topRight.toString(), "BL:", bottomLeft.toString(), "BR:", bottomRight.toString());

	const group =  objectGroup()
		.addRelative(
			line(topLeft, bottomRight, color, lineWidth), vector2d(0, 0)
		)
		.addRelative(
			line(bottomLeft, topRight, color, lineWidth), vector2d(0, 0)
		);

	logger("Cross group:", group.children().map(value => {
		return [value.object(), value.position()]
	}));

	return group;
}

export function isPdfLine(object: any): object is PdfLine {
	return object instanceof PdfLineImpl;
}